import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import api from "@/lib/api";
import {
  Mail, Clock, Send, Check, Loader2, Plus, X,
  FileText, ToggleLeft, ToggleRight
} from "lucide-react";

export default function ReportSettings({ hubId, jwt, lang }) {
  const { t } = useTranslation(lang || "en");
  const [schedule, setSchedule] = useState({ enabled: false, recipients: [], frequency: "daily" });
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const loadSchedule = useCallback(async () => {
    try {
      const data = await api.getReportSchedule(jwt, hubId);
      setSchedule({
        enabled: data.enabled ?? false,
        recipients: data.recipients ?? [],
        frequency: data.frequency ?? "daily",
      });
    } catch { void 0; }
  }, [jwt, hubId]);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.setReportSchedule(jwt, { hub_id: hubId, ...schedule });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { void 0; }
    setSaving(false);
  };

  const handleSendNow = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const res = await api.sendReportNow(jwt, hubId);
      setSendResult(res);
    } catch (e) {
      setSendResult({ error: e?.response?.data?.detail || "Failed to send" });
    }
    setSending(false);
  };

  const addRecipient = () => {
    if (newEmail && newEmail.includes("@") && !schedule.recipients.includes(newEmail.trim())) {
      setSchedule({ ...schedule, recipients: [...schedule.recipients, newEmail.trim()] });
      setNewEmail("");
    }
  };

  const removeRecipient = (email) => {
    setSchedule({ ...schedule, recipients: schedule.recipients.filter(r => r !== email) });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="report-settings">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
            <FileText className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">
              {t("scheduled_reports") || "Scheduled Reports"}
            </h3>
            <p className="text-xs text-slate-400 font-medium">
              {t("report_desc") || "Auto-send PDF summaries to your inbox"}
            </p>
          </div>
        </div>
        <button data-testid="report-toggle" onClick={() => setSchedule({ ...schedule, enabled: !schedule.enabled })}
          className="text-slate-400 hover:text-red-500 transition-colors">
          {schedule.enabled
            ? <ToggleRight className="w-8 h-8 text-red-500" />
            : <ToggleLeft className="w-8 h-8" />}
        </button>
      </div>

      {schedule.enabled && (
        <div className="space-y-4 animate-fadeInUp">
          {/* Frequency */}
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">
              <Clock className="w-3 h-3 inline mr-1" />
              {t("frequency") || "Frequency"}
            </label>
            <div className="flex gap-2">
              {["daily", "weekly"].map((f) => (
                <button key={f} data-testid={`freq-${f}`}
                  onClick={() => setSchedule({ ...schedule, frequency: f })}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                    schedule.frequency === f
                      ? "bg-red-600 text-white shadow-sm"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}>
                  {t(`freq_${f}`) || f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Recipients */}
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">
              <Mail className="w-3 h-3 inline mr-1" />
              {t("recipients") || "Recipients"}
            </label>
            <div className="flex gap-2 mb-2">
              <input data-testid="report-email-input" type="email" value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addRecipient()}
                placeholder="planner@company.com"
                className="flex-1 px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium placeholder:text-slate-300 focus:border-red-400 outline-none" />
              <button data-testid="add-recipient-btn" onClick={addRecipient}
                className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors">
                <Plus className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            {schedule.recipients.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {schedule.recipients.map((email) => (
                  <span key={email} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-xs font-medium text-slate-600">
                    <Mail className="w-3 h-3 text-slate-400" />
                    {email}
                    <button onClick={() => removeRecipient(email)} className="text-slate-400 hover:text-red-500 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {schedule.recipients.length === 0 && (
              <p className="text-xs text-slate-400 italic">{t("report_default_planner") || "Defaults to planner email if empty"}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <button data-testid="save-report-settings" onClick={handleSave} disabled={saving}
              className="px-5 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-xl text-xs font-black transition-all disabled:opacity-50 flex items-center gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : null}
              {saved ? (t("saved") || "Saved") : (t("save_settings") || "Save Settings")}
            </button>
            <button data-testid="send-report-now" onClick={handleSendNow} disabled={sending}
              className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all disabled:opacity-50 flex items-center gap-1.5">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {t("send_now") || "Send Now"}
            </button>
          </div>

          {/* Send result */}
          {sendResult && (
            <div className={`p-3 rounded-xl text-xs font-medium ${sendResult.error ? "bg-red-50 text-red-600 border border-red-200" : "bg-lime-50 text-lime-700 border border-lime-200"}`}
              data-testid="send-result">
              {sendResult.error ? sendResult.error : `Report sent to ${sendResult.results?.length || 0} recipient(s)`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
